import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowDownToLine } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../../components/PageHeader";
import { DataTable } from "../../components/DataTable";
import { StatusBadge } from "../../components/Badges";
import { EmptyState, TableSkeleton } from "../../components/EmptyState";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { api, apiError } from "../../lib/api";
import { formatDate, formatINR } from "../../lib/format";

const STATUSES = ["Pending", "Approved", "Rejected", "Completed"];
const METHODS = ["UPI", "Cash"];

export default function Withdrawals() {
  const [state, setState] = useState({ items: [], total: 0, pages: 1, totalAmount: 0 });
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("all");
  const [method, setMethod] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/client/withdrawals", {
        params: {
          page,
          limit: 10,
          ...(status !== "all" ? { status } : {}),
          ...(method !== "all" ? { method } : {}),
        },
      });
      setState(data);
    } catch (error) {
      toast.error(apiError(error, "Could not load withdrawal history"));
    } finally {
      setLoading(false);
    }
  }, [page, status, method]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo(
    () => [
      { accessorKey: "date", header: "Date", cell: ({ row }) => formatDate(row.original.date) },
      { accessorKey: "method", header: "Method" },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        accessorKey: "amount",
        header: "Amount",
        meta: { numeric: true },
        cell: ({ row }) => formatINR(row.original.amount),
      },
    ],
    []
  );

  return (
    <div className="space-y-5">
      <PageHeader
        title="Withdrawal history"
        subtitle={`${state.total} request${state.total === 1 ? "" : "s"} · ${formatINR(
          state.totalAmount
        )} total`}
        actions={
          <div className="flex flex-wrap gap-2">
            <Select
              value={status}
              onValueChange={(v) => {
                setStatus(v);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[150px]" data-testid="withdrawals-status-filter">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={method}
              onValueChange={(v) => {
                setMethod(v);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[140px]" data-testid="withdrawals-method-filter">
                <SelectValue placeholder="All methods" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All methods</SelectItem>
                {METHODS.map((m) => (
                  <SelectItem key={m} value={m}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        }
      />

      {loading ? (
        <TableSkeleton cols={4} />
      ) : state.items.length === 0 ? (
        <EmptyState
          icon={ArrowDownToLine}
          title="No withdrawal records"
          description="Withdrawal requests processed by your manager will appear here with their status."
          testId="withdrawals-empty-state"
        />
      ) : (
        <DataTable
          columns={columns}
          data={state.items}
          page={page}
          pages={state.pages}
          total={state.total}
          onPageChange={setPage}
          testId="withdrawals-table"
          rowTestId="withdrawal-row"
        />
      )}
    </div>
  );
}
